import { useMemo } from 'react';

import { useTheme } from '@mui/material';
import { ReferenceArea as RechartsReferenceArea } from 'recharts';

import { ReferenceAreaProps } from './types';
import { getThemeColor } from '../../../../utils';
import { tokens } from '../../../../theme/generated/tokens';
import { useGradient } from '../_hooks/useGradient';

export const ReferenceArea = <
  T,
  XV extends string | number = string | number,
  YV extends string | number = string | number
>({
  fill = tokens.theme.data.referenceArea,
  fillOpacity = 0.15,
  getFillByPoint,
  fillTransition = 'sharp',
  stroke = 'none',
  strokeWidth = 1,
  strokeOpacity = 1,
  testId = 'chart-reference-area',
  ...rest
}: ReferenceAreaProps<T, XV, YV>) => {
  const theme = useTheme();
  const themedFill = getThemeColor(theme, fill);
  const themedStroke = getThemeColor(theme, stroke);

  const getStopStylesByPoint = useMemo(() => {
    if (!getFillByPoint) {
      return;
    }
    return (dataPoint: T, index: number) => {
      const styles = getFillByPoint(dataPoint, index);
      return {
        stopColor: styles?.fill
          ? getThemeColor(theme, styles.fill)
          : themedFill,
        stopOpacity: styles?.fillOpacity ?? fillOpacity,
        transition: styles?.fillTransition,
      };
    };
  }, [getFillByPoint, theme, themedFill, fillOpacity]);

  const { gradientStops, gradientId } = useGradient(
    getStopStylesByPoint,
    fillTransition
  );
  const hasGradient = gradientStops.length > 0;
  const styledFill = hasGradient ? `url(#${gradientId})` : themedFill;

  return (
    <>
      {hasGradient && (
        <defs>
          <linearGradient id={gradientId}>{gradientStops}</linearGradient>
        </defs>
      )}
      <RechartsReferenceArea
        {...rest}
        fill={styledFill}
        fillOpacity={fillOpacity}
        stroke={themedStroke}
        strokeWidth={strokeWidth}
        strokeOpacity={strokeOpacity}
        data-testid={testId}
      />
    </>
  );
};
